/**
 * InboxBulkBar — bulk-action toolbar.
 * Shown above the email list when one or more emails are selected.
 */

import { Archive, EnvelopeOpen, Envelope, Trash, X } from '@phosphor-icons/react';

interface Props {
  count: number;
  onArchive: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
  onClear: () => void;
}

export function InboxBulkBar({ count, onArchive, onMarkRead, onMarkUnread, onDelete, onClear }: Props) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: '8px 12px',
      background: '#E8F0FE',
      borderBottom: '1px solid #c5d4f0',
      flexShrink: 0,
    }}>
      {/* Clear selection */}
      <button
        onClick={onClear}
        title="Clear selection"
        style={{
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: 4,
          background: 'transparent',
          color: '#1A73E8',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>

      {/* Count */}
      <span style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 13,
        fontWeight: 500,
        color: '#1A73E8',
        minWidth: 20,
        marginRight: 4,
      }}>
        {count} selected
      </span>

      <div style={{ width: 1, height: 20, background: '#c5d4f0', margin: '0 4px' }} />

      {/* Archive */}
      <BulkBtn title="Archive" onClick={onArchive}>
        <Archive size={16} />
        <span>Archive</span>
      </BulkBtn>

      {/* Mark Read */}
      <BulkBtn title="Mark as read" onClick={onMarkRead}>
        <EnvelopeOpen size={16} />
        <span>Read</span>
      </BulkBtn>

      {/* Mark Unread */}
      <BulkBtn title="Mark as unread" onClick={onMarkUnread}>
        <Envelope size={16} />
        <span>Unread</span>
      </BulkBtn>

      <div style={{ width: 1, height: 20, background: '#c5d4f0', margin: '0 4px' }} />

      {/* Delete */}
      <BulkBtn title="Move to trash" onClick={onDelete} danger>
        <Trash size={16} />
        <span>Delete</span>
      </BulkBtn>
    </div>
  );
}

function BulkBtn({
  title, onClick, danger, children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        border: '1px solid transparent',
        borderRadius: 4,
        background: 'transparent',
        color: danger ? '#dc2626' : '#374151',
        cursor: 'pointer',
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 12,
        fontWeight: 500,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = danger ? '#fef2f2' : '#d2e3fc';
        (e.currentTarget as HTMLButtonElement).style.borderColor = danger ? '#fca5a5' : '#b3cef5';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
      }}
    >
      {children}
    </button>
  );
}
